// src/components/Forum/PostDetail.jsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getPostById } from '../../services/forumApi';
import { getProviderForEdit } from '../../services/providersApi';
import { useAuth } from '../../utils/AuthContext';
import { toast } from 'react-toastify';
import { ArrowLeft } from 'lucide-react';
import PostCard from './PostCard';
import './PostsList.css';

const PostDetail = () => {
    const { postId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { userData } = useAuth();

    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Es proveedor (servicios o productos) si tiene providerRef/providerType.
    // El JWT de Firebase no incluye custom claim providerId, por eso miramos userData.
    const isProvider = !!(userData && (userData.providerRef || userData.providerType));
    // Solo proveedores ACTIVOS pueden contactar / ver el teléfono del autor.
    const [isActiveProvider, setIsActiveProvider] = useState(null);

    useEffect(() => {
        if (!isProvider || !userData?.providerRef) {
            setIsActiveProvider(false);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const prov = await getProviderForEdit(userData.providerRef);
                if (!cancelled) setIsActiveProvider(prov?.active === true);
            } catch (err) {
                console.warn('[PostDetail] no se pudo verificar estado active del provider:', err);
                if (!cancelled) setIsActiveProvider(false);
            }
        })();
        return () => { cancelled = true; };
    }, [isProvider, userData?.providerRef]);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            try {
                setLoading(true);
                const data = await getPostById(postId, true);
                if (!cancelled) setPost(data);
            } catch (e) {
                console.error('Error loading post:', e);
                if (!cancelled) {
                    if (e.response?.status === 404) {
                        setError('Este post ya no existe o fue eliminado.');
                    } else {
                        setError('No pudimos cargar el post. Probá de nuevo más tarde.');
                        toast.error('Error al cargar el post');
                    }
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => { cancelled = true; };
    }, [postId]);

    const handleInterestAdded = () => {
        setPost(prev => prev ? { ...prev, totalInterested: (prev.totalInterested || 0) + 1 } : prev);
    };

    const goBack = () => {
        if (location.key && location.key !== 'default') {
            navigate(-1);
        } else {
            navigate('/');
        }
    };

    return (
        <div className="forum-posts-list-container" style={{ maxWidth: 720, margin: '0 auto', padding: '20px 16px' }}>
            <button
                onClick={goBack}
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    background: 'transparent',
                    border: 'none',
                    color: '#0d6efd',
                    cursor: 'pointer',
                    padding: '8px 0',
                    marginBottom: 12,
                    fontSize: 14
                }}
            >
                <ArrowLeft size={16} /> Volver
            </button>

            {loading && (
                <div className="forum-posts-loading-shimmer" style={{ height: 240 }} />
            )}

            {!loading && error && (
                <div style={{ textAlign: 'center', padding: '40px 16px', color: '#6c757d' }}>
                    <h3>{error}</h3>
                </div>
            )}

            {!loading && post && (
                <div className="post-detail-highlight" style={{
                    border: '2px solid #0d6efd',
                    borderRadius: 12,
                    padding: 4,
                    boxShadow: '0 4px 16px rgba(13,110,253,0.15)'
                }}>
                    <PostCard
                        post={post}
                        isProvider={isProvider}
                        isActiveProvider={isActiveProvider === true}
                        onInterestAdded={handleInterestAdded}
                    />
                </div>
            )}
        </div>
    );
};

export default PostDetail;
